import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, UserIcon, CreditCardIcon, FileTextIcon, MessageSquareIcon } from 'lucide-react';
import { Refund, RefundStatus } from '../types/Refund';
export function RefundDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalNotes, setApprovalNotes] = useState('');
  // Mock data - in a real app, this would be fetched from an API
  const refund: Refund = {
    id: id || '1',
    refundId: 'REF-2024-001',
    transactionId: 'TXN-2024-1234',
    originalAmount: 125000,
    refundAmount: 125000,
    currency: 'MYR',
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested cancellation due to order error',
    requestedBy: 'John Smith',
    requestedAt: '2024-01-18T10:30:00Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4242',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    notes: 'Customer called support to request refund. Order was placed by mistake.',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-18T10:30:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'John Smith'
    }, {
      id: '2',
      timestamp: '2024-01-18T10:35:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  };
  const getStatusBadge = (status: RefundStatus | string) => {
    const styles: Record<string, string> = {
      requested: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    const labels: Record<string, string> = {
      requested: 'Requested',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      processing: 'Processing',
      completed: 'Completed',
      rejected: 'Rejected',
      failed: 'Failed',
      cancelled: 'Cancelled',
      pending: 'Pending'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-7860">
        {labels[status] || status}
      </span>;
  };
  const handleApproval = () => {
    // In a real app, this would make an API call
    console.log(`${approvalAction} refund with notes:`, approvalNotes);
    setShowApprovalModal(false);
    // Navigate back or show success message
  };
  return <div className="space-y-6" data-id="element-7861">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-7862">
        <button onClick={() => navigate('/refunds')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-7863">
          <ArrowLeftIcon size={20} data-id="element-7864" />
          Back to Refunds
        </button>
        {refund.status === 'pending_approval' && <div className="flex gap-2" data-id="element-7865">
            <button onClick={() => {
          setApprovalAction('reject');
          setShowApprovalModal(true);
        }} className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50" data-id="element-7866">
              <XCircleIcon size={18} data-id="element-7867" />
              Reject
            </button>
            <button onClick={() => {
          setApprovalAction('approve');
          setShowApprovalModal(true);
        }} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-7868">
              <CheckCircleIcon size={18} data-id="element-7869" />
              Approve
            </button>
          </div>}
      </div>
      {/* Refund Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7870">
        <div className="flex items-start justify-between mb-4" data-id="element-7871">
          <div data-id="element-7872">
            <div className="flex items-center gap-3 mb-2" data-id="element-7873">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-7874">
                {refund.refundId}
              </h1>
              {refund.isPartial && <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded" data-id="element-7875">
                  Partial Refund
                </span>}
            </div>
            <div className="text-sm text-gray-500" data-id="element-7876">
              Transaction: {refund.transactionId}
            </div>
          </div>
          {getStatusBadge(refund.status)}
        </div>
        {/* Key Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-7877">
          <div data-id="element-7878">
            <div className="text-sm text-gray-500 mb-1" data-id="element-7879">Refund Amount</div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-7880">
              MYR {refund.refundAmount.toLocaleString('en-MY')}
            </div>
            {refund.isPartial && <div className="text-sm text-gray-500 mt-1" data-id="element-7881">
                of MYR {refund.originalAmount.toLocaleString('en-MY')} original
              </div>}
          </div>
          <div data-id="element-7882">
            <div className="text-sm text-gray-500 mb-1" data-id="element-7883">Requested By</div>
            <div className="text-lg font-medium text-gray-900" data-id="element-7884">
              {refund.requestedBy}
            </div>
            <div className="text-sm text-gray-500" data-id="element-7885">
              {new Date(refund.requestedAt).toLocaleString('en-GB')}
            </div>
          </div>
          <div data-id="element-7886">
            <div className="text-sm text-gray-500 mb-1" data-id="element-7887">Merchant</div>
            <div className="text-lg font-medium text-gray-900" data-id="element-7888">
              {refund.merchantName}
            </div>
            <div className="text-sm text-gray-500" data-id="element-7889">{refund.merchantId}</div>
          </div>
        </div>
      </div>
      {/* Customer & Payment Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7890">
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7891">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-7892">
            <UserIcon size={20} className="text-blue-600" data-id="element-7893" />
            Customer Information
          </h3>
          <div className="space-y-3" data-id="element-7894">
            <div data-id="element-7895">
              <div className="text-sm text-gray-500" data-id="element-7896">Name</div>
              <div className="text-base font-medium text-gray-900" data-id="element-7897">
                {refund.customerName}
              </div>
            </div>
            <div data-id="element-7898">
              <div className="text-sm text-gray-500" data-id="element-7899">Email</div>
              <div className="text-base font-medium text-gray-900" data-id="element-7900">
                {refund.customerEmail}
              </div>
            </div>
            <div data-id="element-7901">
              <div className="text-sm text-gray-500" data-id="element-7902">Payment Method</div>
              <div className="flex items-center gap-2" data-id="element-7903">
                <CreditCardIcon size={16} className="text-gray-400" data-id="element-7904" />
                <span className="text-base font-medium text-gray-900" data-id="element-7905">
                  {refund.paymentMethod}{' '}
                  {refund.cardLast4 && `•••• ${refund.cardLast4}`}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7906">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-7907">
            <FileTextIcon size={20} className="text-blue-600" data-id="element-7908" />
            Refund Reason
          </h3>
          <div className="space-y-3" data-id="element-7909">
            <div data-id="element-7910">
              <div className="text-sm text-gray-500" data-id="element-7911">Category</div>
              <div className="text-base font-medium text-gray-900 capitalize" data-id="element-7912">
                {refund.reason.replace(/_/g, ' ')}
              </div>
            </div>
            {refund.reasonDescription && <div data-id="element-7913">
                <div className="text-sm text-gray-500" data-id="element-7914">Description</div>
                <div className="text-base text-gray-900" data-id="element-7915">
                  {refund.reasonDescription}
                </div>
              </div>}
            {refund.notes && <div data-id="element-7916">
                <div className="text-sm text-gray-500" data-id="element-7917">Additional Notes</div>
                <div className="text-base text-gray-900" data-id="element-7918">{refund.notes}</div>
              </div>}
          </div>
        </div>
      </div>
      {/* Approval Workflow */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7919">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-7920">
          Approval Workflow
        </h3>
        <div className="space-y-4" data-id="element-7921">
          {refund.approvalWorkflow.map((step, index) => <div key={step.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg" data-id="element-7922">
              <div className="flex-shrink-0" data-id="element-7923">
                {step.status === 'approved' ? <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-7924">
                    <CheckCircleIcon size={20} className="text-green-600" data-id="element-7925" />
                  </div> : step.status === 'rejected' ? <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-7926">
                    <XCircleIcon size={20} className="text-red-600" data-id="element-7927" />
                  </div> : <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-7928">
                    <ClockIcon size={20} className="text-yellow-600" data-id="element-7929" />
                  </div>}
              </div>
              <div className="flex-1" data-id="element-7930">
                <div className="flex items-center justify-between mb-2" data-id="element-7931">
                  <div data-id="element-7932">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7933">
                      {step.approverName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-7934">
                      {step.approverEmail}
                    </div>
                  </div>
                  {getStatusBadge(step.status)}
                </div>
                {step.timestamp && <div className="text-sm text-gray-500 mb-2" data-id="element-7935">
                    {new Date(step.timestamp).toLocaleString('en-GB')}
                  </div>}
                {step.notes && <div className="mt-2 p-3 bg-gray-50 rounded text-sm text-gray-700" data-id="element-7936">
                    {step.notes}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      {/* Lifecycle Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7937">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-7938">
          Refund Timeline
        </h3>
        <div className="space-y-4" data-id="element-7939">
          {refund.lifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-7940">
              <div className="flex flex-col items-center" data-id="element-7941">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === refund.lifecycle.length - 1 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-7942">
                  {event.status === 'completed' ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-7943" /> : event.status === 'rejected' || event.status === 'failed' ? <XCircleIcon size={16} className="text-red-600" data-id="element-7944" /> : <ClockIcon size={16} className={index === refund.lifecycle.length - 1 ? 'text-blue-600' : 'text-gray-400'} data-id="element-7945" />}
                </div>
                {index < refund.lifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-7946"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-7947">
                <div className="text-sm font-medium text-gray-900" data-id="element-7948">
                  {event.description}
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-7949">
                  {new Date(event.timestamp).toLocaleString('en-GB')}
                </div>
                {event.actor && <div className="text-xs text-gray-500 mt-1" data-id="element-7950">
                    By: {event.actor}
                  </div>}
                {event.metadata && <div className="mt-2 p-2 bg-gray-50 rounded text-xs" data-id="element-7951">
                    {Object.entries(event.metadata).map(([key, value]) => <div key={key} data-id="element-7952">
                        <span className="font-medium" data-id="element-7953">
                          {key.replace(/_/g, ' ')}:
                        </span>{' '}
                        {String(value)}
                      </div>)}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      {/* Approval Modal */}
      {showApprovalModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-7954">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-7955">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowApprovalModal(false)} data-id="element-7956">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-7957"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-7958">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-7959">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-7960">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-7961">
                  {approvalAction === 'approve' ? 'Approve Refund' : 'Reject Refund'}
                </h3>
                <div className="space-y-4" data-id="element-7962">
                  <div data-id="element-7963">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7964">
                      Notes
                    </label>
                    <textarea rows={4} value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={approvalAction === 'approve' ? 'Add any notes about this approval...' : 'Provide a reason for rejection...'} data-id="element-7965" />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-7966">
                <button type="button" className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'}`} onClick={handleApproval} data-id="element-7967">
                  {approvalAction === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowApprovalModal(false)} data-id="element-7968">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}